import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AdminMovieDebug,
  AdminMovieSearchResult,
  adminGetMovieDebug,
  adminSearchMovies,
} from '../api';

function JsonPanel({
  title,
  data,
  defaultOpen,
}: {
  title: string;
  data: unknown;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);

  if (data === null || data === undefined) {
    return (
      <div style={{ marginBottom: '0.75rem' }}>
        <button
          onClick={() => setOpen(!open)}
          style={{
            background: 'none',
            border: 'none',
            color: '#a0a0a0',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            padding: '4px 0',
          }}
        >
          {open ? '▾' : '▸'} {title} <span style={{ fontWeight: 400 }}>(empty)</span>
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: 'none',
          border: 'none',
          color: '#e0e0e0',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 600,
          padding: '4px 0',
        }}
      >
        {open ? '▾' : '▸'} {title}
      </button>
      {open && (
        <pre
          style={{
            background: '#1a1a2e',
            borderRadius: '6px',
            padding: '12px',
            fontSize: '12px',
            overflow: 'auto',
            maxHeight: '500px',
            marginTop: '4px',
            color: '#c8d6e5',
            lineHeight: 1.4,
          }}
        >
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default function AdminMovieSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AdminMovieSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [debugData, setDebugData] = useState<Record<number, AdminMovieDebug>>({});
  const [debugLoading, setDebugLoading] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const search = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    setError(null);
    try {
      const data = await adminSearchMovies(q);
      setResults(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => search(query.trim()), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  async function handleToggle(tmdbId: number) {
    if (expandedId === tmdbId) {
      setExpandedId(null);
      return;
    }

    setExpandedId(tmdbId);

    if (!debugData[tmdbId]) {
      setDebugLoading(tmdbId);
      try {
        const data = await adminGetMovieDebug(tmdbId);
        setDebugData((prev) => ({ ...prev, [tmdbId]: data }));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load debug data');
        setExpandedId(null);
      } finally {
        setDebugLoading(null);
      }
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <Link to="/admin" style={{ color: '#4b7bec', textDecoration: 'none', fontSize: '14px' }}>
          &larr; Admin
        </Link>
        <h2 style={{ margin: 0 }}>Movie Search</h2>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search movies by title..."
          style={{
            width: '100%',
            padding: '10px 12px',
            fontSize: '16px',
            borderRadius: '6px',
            border: '1px solid #333',
            background: '#1a1a2e',
            color: '#e0e0e0',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          autoFocus
        />
        {searching && (
          <p style={{ fontSize: '13px', opacity: 0.5, marginTop: '8px', marginBottom: 0 }}>
            Searching...
          </p>
        )}
      </div>

      {error && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <p className="error">{error}</p>
        </div>
      )}

      {results.length > 0 && (
        <div>
          {results.map((movie) => (
            <div key={movie.tmdb_id} className="card" style={{ marginBottom: '0.75rem' }}>
              <div
                onClick={() => handleToggle(movie.tmdb_id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                }}
              >
                {movie.poster_url ? (
                  <img
                    src={movie.poster_url}
                    alt=""
                    style={{
                      width: '40px',
                      height: '60px',
                      borderRadius: '4px',
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '40px',
                      height: '60px',
                      borderRadius: '4px',
                      background: '#2a2a3e',
                      flexShrink: 0,
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '15px' }}>
                    {movie.title}
                    {movie.year && (
                      <span style={{ fontWeight: 400, opacity: 0.5, marginLeft: '6px' }}>
                        ({movie.year})
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.4 }}>TMDB ID: {movie.tmdb_id}</div>
                </div>
                <span style={{ fontSize: '12px', opacity: 0.4 }}>
                  {expandedId === movie.tmdb_id ? '▾' : '▸'}
                </span>
              </div>

              {expandedId === movie.tmdb_id && (
                <div style={{ marginTop: '12px', borderTop: '1px solid #2a2a3e', paddingTop: '12px' }}>
                  {debugLoading === movie.tmdb_id ? (
                    <p style={{ fontSize: '13px', opacity: 0.5 }}>Loading debug data...</p>
                  ) : debugData[movie.tmdb_id] ? (
                    <>
                      <JsonPanel
                        title="DB: movie_cache"
                        data={debugData[movie.tmdb_id].db.movie_cache}
                        defaultOpen
                      />
                      <JsonPanel
                        title={`DB: playlist_movies (${debugData[movie.tmdb_id].db.playlist_movies.length})`}
                        data={
                          debugData[movie.tmdb_id].db.playlist_movies.length > 0
                            ? debugData[movie.tmdb_id].db.playlist_movies
                            : null
                        }
                      />
                      <JsonPanel title="TMDB Raw" data={debugData[movie.tmdb_id].tmdb_raw} />
                      <JsonPanel title="OMDB Raw" data={debugData[movie.tmdb_id].omdb_raw} />
                    </>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {query.length >= 2 && !searching && results.length === 0 && !error && (
        <div className="card">
          <p style={{ opacity: 0.6 }}>No results found.</p>
        </div>
      )}
    </div>
  );
}
